import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActionMenuComponent, HoverIdComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { StatusBadgeComponent } from '../../../shared/ui/status-badge/status-badge.component';
import { StatusBadgeViewModel } from '../../../shared/ui/status-badge/status-badge.models';
import { SpecialistDowntimeItem } from '../data-access/specialist-downtime.models';

@Component({
  selector: 'app-specialist-downtime-table',
  standalone: true,
  imports: [StatusBadgeComponent, TranslatePipe, ActionMenuComponent, HoverIdComponent],
  template: `
    <div class="downtime-table">
      <div class="downtime-table__desktop">
        <table class="downtime-table__table">
          <thead>
            <tr>
              <th>{{ 'Clinic' | t }}</th>
              @if (showSpecialistColumn) {
                <th>{{ 'Specialist' | t }}</th>
              }
              <th>Downtime Window</th>
              <th>{{ 'Actual End Time' | t }}</th>
              <th class="downtime-table__impact-heading">Impact</th>
              <th>{{ 'common.status' | t }}</th>
              <th>{{ 'common.actions' | t }}</th>
            </tr>
          </thead>

          <tbody>
            @for (item of items; track item.id) {
              <tr
                class="downtime-table__row"
                tabindex="0"
                (click)="onRowSelected($event, item)"
                (keydown.enter)="onRowSelected($event, item)"
                (keydown.space)="onRowSelectedFromSpace($event, item)"
              >
                <td>
                  <lib-hover-id [value]="item.clinicId">
                    <span [title]="clinicLabel(item)">{{ clinicLabel(item) }}</span>
                  </lib-hover-id>
                </td>
                @if (showSpecialistColumn) {
                  <td>
                    <lib-hover-id [value]="item.specialistUserId">
                      @if (canOpenSpecialist) {
                        <button
                          type="button"
                          class="downtime-table__link-button"
                          (click)="$event.stopPropagation(); specialistOpenClicked.emit(item.specialistUserId)"
                          [title]="specialistLabel(item)"
                        >
                          {{ specialistLabel(item) }}
                        </button>
                      } @else {
                        <span [title]="specialistLabel(item)">
                          {{ specialistLabel(item) }}
                        </span>
                      }
                    </lib-hover-id>
                  </td>
                }
                <td>{{ formatWindow(item.startTime, item.expectedRepairUntil, item.clinicTimeZone) }}</td>
                <td>
                  <div class="downtime-table__actual-end">
                    <span>{{ formatAsLocalNaiveDateTime(item.actualEndTime, item.clinicTimeZone) }}</span>
                    @if (item.isOverdue) {
                      <span class="downtime-table__overdue-badge">{{ overdueLabel(item) }}</span>
                    }
                  </div>
                </td>
                <td class="downtime-table__impact-cell">
                  <span
                    class="downtime-table__impact-dot"
                    [class.downtime-table__impact-dot--resolved]="isImpactResolved(item)"
                    [class.downtime-table__impact-dot--action-required]="!isImpactResolved(item)"
                    [attr.title]="impactTitle(item)"
                    [attr.aria-label]="impactTitle(item)"
                  ></span>
                </td>
                <td>
                  <app-status-badge
                    [label]="getStatusViewModel(item.status).label"
                    [tone]="getStatusViewModel(item.status).tone"
                  />
                </td>
                <td (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
                  @if (hasAvailableActions(item)) {
                    <lib-action-menu [menuTemplate]="tableActionsMenu" buttonAriaLabel="Open specialist downtime actions">
                      <ng-template #tableActionsMenu let-close="close">
                        @if (canFinishDowntime && canResolveDowntime(item)) {
                          <button
                            type="button"
                            class="action-menu__item action-menu__item--primary"
                            [disabled]="busyItemId === item.id"
                            (click)="resolveClicked.emit(item); close()"
                          >
                            Finish downtime
                          </button>
                        }

                        @if (canManageDowntime) {
                          <button
                            type="button"
                            [disabled]="busyItemId === item.id"
                            class="action-menu__item action-menu__item--danger"
                            (click)="deleteClicked.emit(item); close()"
                          >
                            {{ 'Delete' | t }}
                          </button>
                        }

                        @if (canModerateRequests && item.status === 'pending_approval') {
                          <button
                            type="button"
                            [disabled]="busyItemId === item.id"
                            class="action-menu__item action-menu__item--primary"
                            (click)="approveClicked.emit(item); close()"
                          >
                            {{ 'action.approve' | t }}
                          </button>

                          <button
                            type="button"
                            [disabled]="busyItemId === item.id"
                            class="action-menu__item action-menu__item--danger"
                            (click)="rejectClicked.emit(item); close()"
                          >
                            {{ 'action.reject' | t }}
                          </button>
                        }
                      </ng-template>
                    </lib-action-menu>
                  } @else {
                    <span class="downtime-table__empty-action">—</span>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="downtime-table__cards">
        @for (item of items; track item.id) {
          <article class="downtime-table__card" (click)="rowClicked.emit(item)">
            <div class="downtime-table__card-head">
              <div class="downtime-table__card-head-main">
                @if (showSpecialistColumn) {
                  @if (canOpenSpecialist) {
                    <lib-hover-id [value]="item.specialistUserId">
                      <button
                        type="button"
                        class="downtime-table__link-button downtime-table__link-button--card"
                        (click)="$event.stopPropagation(); specialistOpenClicked.emit(item.specialistUserId)"
                        [title]="specialistLabel(item)"
                      >
                        {{ specialistLabel(item) }}
                      </button>
                    </lib-hover-id>
                  } @else {
                    <lib-hover-id [value]="item.specialistUserId">
                      <div class="downtime-table__card-title">{{ specialistLabel(item) }}</div>
                    </lib-hover-id>
                  }
                  <lib-hover-id [value]="item.clinicId">
                    <div class="downtime-table__card-subtitle">{{ clinicLabel(item) }}</div>
                  </lib-hover-id>
                } @else {
                  <lib-hover-id [value]="item.clinicId">
                    <div class="downtime-table__card-title">{{ clinicLabel(item) }}</div>
                  </lib-hover-id>
                }
              </div>
              <div class="downtime-table__card-head-side" (click)="$event.stopPropagation()">
                <span
                  class="downtime-table__impact-dot"
                  [class.downtime-table__impact-dot--resolved]="isImpactResolved(item)"
                  [class.downtime-table__impact-dot--action-required]="!isImpactResolved(item)"
                  [attr.title]="impactTitle(item)"
                  [attr.aria-label]="impactTitle(item)"
                ></span>
                <app-status-badge
                  [label]="getStatusViewModel(item.status).label"
                  [tone]="getStatusViewModel(item.status).tone"
                />
                @if (hasAvailableActions(item)) {
                  <lib-action-menu [menuTemplate]="cardActionsMenu" buttonAriaLabel="Open specialist downtime actions">
                    <ng-template #cardActionsMenu let-close="close">
                      @if (canFinishDowntime && canResolveDowntime(item)) {
                        <button
                          type="button"
                          class="action-menu__item action-menu__item--primary"
                          [disabled]="busyItemId === item.id"
                          (click)="resolveClicked.emit(item); close()"
                        >
                          Finish downtime
                        </button>
                      }

                      @if (canManageDowntime) {
                        <button
                          type="button"
                          [disabled]="busyItemId === item.id"
                          class="action-menu__item action-menu__item--danger"
                          (click)="deleteClicked.emit(item); close()"
                        >
                          {{ 'Delete' | t }}
                        </button>
                      }

                      @if (canModerateRequests && item.status === 'pending_approval') {
                        <button
                          type="button"
                          [disabled]="busyItemId === item.id"
                          class="action-menu__item action-menu__item--primary"
                          (click)="approveClicked.emit(item); close()"
                        >
                          {{ 'action.approve' | t }}
                        </button>

                        <button
                          type="button"
                          [disabled]="busyItemId === item.id"
                          class="action-menu__item action-menu__item--danger"
                          (click)="rejectClicked.emit(item); close()"
                        >
                          {{ 'action.reject' | t }}
                        </button>
                      }
                    </ng-template>
                  </lib-action-menu>
                }
              </div>
            </div>

            <div class="downtime-table__card-grid">
              <div class="downtime-table__card-row">
                <div class="downtime-table__card-label">Downtime Window</div>
                <div class="downtime-table__card-value">{{ formatWindow(item.startTime, item.expectedRepairUntil, item.clinicTimeZone) }}</div>
              </div>
              <div class="downtime-table__card-row">
                <div class="downtime-table__card-label">{{ 'Actual End Time' | t }}</div>
                <div class="downtime-table__card-value">
                  <div class="downtime-table__actual-end">
                    <span>{{ formatAsLocalNaiveDateTime(item.actualEndTime, item.clinicTimeZone) }}</span>
                    @if (item.isOverdue) {
                      <span class="downtime-table__overdue-badge">{{ overdueLabel(item) }}</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          </article>
        }
      </div>
    </div>
  `,
  styles: [`
    .downtime-table {
      background: #ffffff;
    }

    .downtime-table__desktop {
      overflow-x: auto;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
    }

    .downtime-table__table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1040px;
    }

    .downtime-table__table th,
    .downtime-table__table td {
      text-align: left;
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
      font-size: 13px;
    }

    .downtime-table__table th {
      background: #f9fafb;
      color: #374151;
    }

    .downtime-table__table td {
      color: #111827;
    }

    .downtime-table__impact-heading,
    .downtime-table__impact-cell {
      width: 64px;
      text-align: center;
    }

    .downtime-table__impact-dot {
      display: inline-flex;
      width: 14px;
      height: 14px;
      border-radius: 999px;
      border: 2px solid #ffffff;
      box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.16);
      vertical-align: middle;
    }

    .downtime-table__impact-dot--resolved {
      background: #22c55e;
    }

    .downtime-table__impact-dot--action-required {
      background: #ef4444;
    }

    .downtime-table__actual-end {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .downtime-table__overdue-badge {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      border: 1px solid #fecaca;
      border-radius: 999px;
      background: #fef2f2;
      color: #991b1b;
      padding: 0 8px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1;
      white-space: nowrap;
    }

    .downtime-table__row {
      cursor: pointer;
      transition: background-color 140ms ease;
    }

    .downtime-table__row:hover,
    .downtime-table__row:focus-visible {
      background: #f8fafc;
      outline: none;
    }

    .downtime-table__link-button {
      all: unset;
      cursor: pointer;
      color: #2563eb;
      text-decoration: underline;
      text-underline-offset: 2px;
      display: inline-block;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .downtime-table__empty-action {
      color: #9ca3af;
      font-size: 14px;
    }

    .downtime-table__cards {
      display: none;
      gap: 14px;
    }

    .downtime-table__card {
      border: 1px solid #e5e7eb;
      border-radius: 18px;
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.95));
      box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
      padding: 16px;
      cursor: pointer;
    }

    .downtime-table__card-head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 14px;
    }

    .downtime-table__card-head-main {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .downtime-table__card-title {
      font-size: 16px;
      font-weight: 700;
      color: #111827;
      overflow-wrap: anywhere;
    }

    .downtime-table__card-subtitle {
      font-size: 13px;
      color: #6b7280;
      overflow-wrap: anywhere;
    }

    .downtime-table__card-head-side {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 10px;
      flex-shrink: 0;
    }

    .downtime-table__card-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px 16px;
    }

    .downtime-table__card-row {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .downtime-table__card-label {
      font-size: 11px;
      line-height: 1.2;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #6b7280;
    }

    .downtime-table__card-value {
      font-size: 14px;
      line-height: 1.45;
      color: #111827;
      overflow-wrap: anywhere;
    }

    @media (max-width: 900px) {
      .downtime-table__desktop {
        display: none;
      }

      .downtime-table__cards {
        display: grid;
      }

      .downtime-table__link-button {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
      }
    }

    @media (max-width: 640px) {
      .downtime-table__card {
        border-radius: 16px;
        padding: 14px;
      }

      .downtime-table__card-head {
        align-items: stretch;
      }

      .downtime-table__card-head-side {
        align-items: flex-start;
      }

      .downtime-table__card-grid {
        grid-template-columns: minmax(0, 1fr);
      }
    }
  `]
})
export class SpecialistDowntimeTableComponent
{
  @Input()
  public items: SpecialistDowntimeItem[] = [];

  @Input()
  public clinics: Array<{ id: string; label: string }> = [];

  @Input()
  public specialists: Array<{ id: string; label: string }> = [];

  @Input()
  public busyItemId: string | null = null;

  @Input()
  public canModerateRequests = false;

  @Input()
  public canManageDowntime = false;

  @Input()
  public canFinishDowntime = false;

  @Input()
  public canOpenSpecialist = false;

  @Input()
  public showSpecialistColumn = true;

  @Output()
  public readonly deleteClicked = new EventEmitter<SpecialistDowntimeItem>();

  @Output()
  public readonly resolveClicked = new EventEmitter<SpecialistDowntimeItem>();

  @Output()
  public readonly approveClicked = new EventEmitter<SpecialistDowntimeItem>();

  @Output()
  public readonly rejectClicked = new EventEmitter<SpecialistDowntimeItem>();

  @Output()
  public readonly specialistOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly rowClicked = new EventEmitter<SpecialistDowntimeItem>();

  protected formatAsLocalNaiveDateTime(value: string | null, timeZone?: string | null): string
  {
    if (!value)
    {
      return '—';
    }

    const normalized = value.trim();
    if (/[zZ]|[+-]\d{2}:\d{2}$/.test(normalized))
    {
      const date = new Date(normalized);
      if (!Number.isNaN(date.getTime()))
      {
        const formatted = this.formatDateInTimeZone(date, timeZone);
        if (formatted)
        {
          return formatted;
        }
      }
    }

    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/
    );

    if (!match)
    {
      return normalized;
    }

    const [, year, month, day, hours, minutes] = match;
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  private formatDateInTimeZone(date: Date, timeZone?: string | null): string | null
  {
    try
    {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timeZone?.trim() || undefined
      });
      const parts = formatter.formatToParts(date);
      const value = (type: string): string =>
        parts.find((part) => part.type === type)?.value ?? '';

      return `${value('day')}.${value('month')}.${value('year')} ${value('hour')}:${value('minute')}`;
    }
    catch
    {
      return null;
    }
  }

  protected getStatusViewModel(status: SpecialistDowntimeItem['status']): StatusBadgeViewModel
  {
    switch (status)
    {
      case 'pending_approval':
        return { label: 'status.pendingApproval', tone: 'warning' };

      case 'active':
        return { label: 'status.active', tone: 'danger' };

      case 'resolved':
        return { label: 'status.resolved', tone: 'success' };

      case 'rejected':
        return { label: 'status.rejected', tone: 'neutral' };

      default:
        return { label: status, tone: 'neutral' };
    }
  }

  protected isImpactResolved(item: SpecialistDowntimeItem): boolean
  {
    return item.impactResolution?.status === 'resolved';
  }

  protected impactTitle(item: SpecialistDowntimeItem): string
  {
    return this.isImpactResolved(item) ? 'Impact resolved' : 'Impact action required';
  }

  protected overdueLabel(item: SpecialistDowntimeItem): string
  {
    const minutes = item.overdueByMinutes ?? 0;
    if (!item.isOverdue || minutes <= 0)
    {
      return 'Overdue';
    }

    if (minutes < 60)
    {
      return `Overdue ${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `Overdue ${hours}h ${remainingMinutes}m`
      : `Overdue ${hours}h`;
  }

  protected readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 12) {
      return `${normalized.slice(0, 8)}...`;
    }

    return normalized;
  }

  protected clinicLabel(item: SpecialistDowntimeItem): string
  {
    return item.clinicName?.trim()
      || this.clinics.find((option) => option.id === item.clinicId)?.label
      || this.readableId(item.clinicId);
  }

  protected specialistLabel(item: SpecialistDowntimeItem): string
  {
    return item.specialistLabel?.trim()
      || this.specialists.find((option) => option.id === item.specialistUserId)?.label
      || this.readableId(item.specialistUserId);
  }

  protected hasAvailableActions(item: SpecialistDowntimeItem): boolean
  {
    return (this.canFinishDowntime && this.canResolveDowntime(item))
      || this.canManageDowntime
      || (this.canModerateRequests && item.status === 'pending_approval');
  }

  protected onRowSelected(event: Event, item: SpecialistDowntimeItem): void
  {
    if (this.isInteractiveEvent(event)) {
      return;
    }

    this.rowClicked.emit(item);
  }

  protected onRowSelectedFromSpace(event: Event, item: SpecialistDowntimeItem): void
  {
    event.preventDefault();
    this.onRowSelected(event, item);
  }

  protected canResolveDowntime(item: SpecialistDowntimeItem): boolean
  {
    return item.status === 'active' || item.status === 'pending_approval';
  }

  protected formatWindow(
    startTime: string | null,
    expectedRepairUntil: string | null,
    timeZone?: string | null,
  ): string
  {
    return `${this.formatAsLocalNaiveDateTime(startTime, timeZone)} -> ${this.formatAsLocalNaiveDateTime(expectedRepairUntil, timeZone)}`;
  }

  private isInteractiveEvent(event: Event): boolean
  {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return !!target.closest('button, a, input, select, textarea, lib-action-menu');
  }
}
